import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  GetApp as DownloadIcon,
  Info as InfoIcon,
  VpnKey as KeyIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LicenseStatusChip from "../components/license/LicenseStatusChip";
import ProductLicenseDialog from "../components/product/ProductLicenseDialog";
import { useAuth } from "../contexts/AuthContext";
import { ThemeContext } from "../contexts/ThemeContext";
import { getProducts } from "../services/productService";
import { userProductLicenseService } from "../services/userProductLicenseService";
import { userProductService } from "../services/userProductService";
import {
  gridSpacing,
  pageStyles,
  responsiveTypography,
} from "../styles/responsive";
import { isLicenseServerAssignment } from "../utils/licenseServerUtils";

const ProductListPage = () => {
  const [products, setProducts] = useState([]);
  const [productLicenses, setProductLicenses] = useState([]);
  const [licenseServerProductIds, setLicenseServerProductIds] = useState(
    () => new Set(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [licenseKey, setLicenseKey] = useState("");
  const [licenseDialogMode, setLicenseDialogMode] = useState("input");
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { themeMode } = useContext(ThemeContext);

  useEffect(() => {
    fetchUserProducts();
  }, []);

  // Fetch licenses when products change
  useEffect(() => {
    if (products.length > 0) {
      fetchUserLicenses();
    }
  }, [products]);

  const fetchUserProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      if (currentUser) {
        // Use the same API that dashboard uses
        const response = await getProducts();
        setProducts(response || []);

        // /products doesn't carry the per-assignment through-flag, so
        // cross-reference against getMyProducts (already used by
        // DashboardPage) to know which assignments are license-server
        // assignments and must have license UI suppressed.
        try {
          const myProductsData = await userProductService.getMyProducts();
          const myProducts = myProductsData.products || [];
          setLicenseServerProductIds(
            new Set(
              myProducts
                .filter((p) => isLicenseServerAssignment(p))
                .map((p) => p.id),
            ),
          );
        } catch (err) {
          console.error("Error fetching my products:", err);
        }
      } else {
        setProducts([]);
      }
    } catch (err) {
      setError(
        `Failed to load your assigned products. Error: ${err.message || err}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchUserLicenses = async () => {
    try {
      // Only fetch if user is logged in
      if (currentUser) {
        const response = await userProductLicenseService.getMyLicenses();
        const licenses = (response.productLicenses || []).filter(
          (license) => !licenseServerProductIds.has(license.product_id),
        );
        setProductLicenses(licenses);
      }
    } catch (err) {
      console.error("Error fetching licenses:", err);
      // Don't set error state here as it might disrupt the main product display
    }
  };

  const handleViewLicense = (product) => {
    // Set the selected product and open the license dialog
    setSelectedProduct(product);
    setShowLicenseDialog(true);
  };

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
  };

  // Helper function to get license for a specific product
  const getProductLicense = (productId) => {
    return productLicenses.find((license) => license.product_id === productId);
  };

  if (loading) {
    return (
      <Container sx={pageStyles.container}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={48} />
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "1rem", sm: "1.25rem" },
              textAlign: "center",
            }}
          >
            Loading your products...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={pageStyles.container}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={fetchUserProducts}
          size={window.innerWidth < 600 ? "medium" : "large"}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container sx={pageStyles.container}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>
      {/* <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}> */}
      <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Avatar sx={{ bgcolor: "primary.main", mr: { xs: 1, sm: 2 } }}>
          <AssignmentIcon />
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              ...responsiveTypography.h4,
              mb: { xs: 0.5, sm: 1 },
            }}
          >
            Your Assigned Products
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Products you have access to download and use
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Chip
          label={`${products.length} product${
            products.length !== 1 ? "s" : ""
          } available`}
          color="primary"
          variant="outlined"
          size={window.innerWidth < 600 ? "small" : "medium"}
        />
        {productLicenses.length > 0 && (
          <Chip
            label={`${productLicenses.length} license${
              productLicenses.length !== 1 ? "s" : ""
            }`}
            color="secondary"
            variant="outlined"
            size={window.innerWidth < 600 ? "small" : "medium"}
            icon={<KeyIcon />}
          />
        )}
      </Box>
      {/* </Paper> */}

      {products.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
          <AssignmentIcon
            sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="h5" gutterBottom>
            No Products Assigned
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You don't have access to any products yet. Please contact your
            administrator to get product access.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<InfoIcon />}
            onClick={() =>
              (window.location.href =
                "mailto:admin@company.com?subject=Product Access Request")
            }
          >
            Contact Administrator
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={gridSpacing.normal}>
          {products.map((product) => {
            const showLicenseUi =
              product.requires_license &&
              !licenseServerProductIds.has(product.id);
            return (
            <Grid
              item
              xs={12}
              sm={6}
              lg={4}
              key={product.id}
              sx={{ display: "flex", width: "100%" }}
            >
              <Card
                elevation={3}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  transition: "all 0.3s ease-in-out",
                  p: 1,
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                {product.banner_image && (
                  <CardMedia
                    component="img"
                    height="100%"
                    image={product.banner_image}
                    alt={`${product.name} banner`}
                    sx={{
                      objectFit: "cover",
                      borderTopLeftRadius: "8px",
                      borderTopRightRadius: "8px",
                    }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    mb={2}
                    flexWrap="wrap"
                  >
                    <Avatar
                      sx={{
                        bgcolor: "secondary.main",
                        mr: { xs: 1, sm: 2 },
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                      }}
                    >
                      {product.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                        flex: 1,
                      }}
                    >
                      {product.name}
                    </Typography>
                  </Box>

                  {product.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {product.description}
                    </Typography>
                  )}

                  {/* License Status - Prominent Display */}
                  {showLicenseUi && (
                    <Box sx={{ mb: 2 }}>
                      <LicenseStatusChip
                        license={getProductLicense(product.id)}
                        size="medium"
                      />
                    </Box>
                  )}

                  <Box
                    sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}
                  >
                    <Chip
                      label="Assigned Product"
                      color="success"
                      size="small"
                    />
                    {showLicenseUi && (
                      <Chip
                        icon={<KeyIcon />}
                        label="License Required"
                        color="secondary"
                        size="small"
                      />
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ p: { xs: 1, sm: 2 }, pt: 0 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleProductClick(product.id)}
                        color="primary"
                        size={window.innerWidth < 600 ? "medium" : "large"}
                        sx={{ py: { xs: 1, sm: 1.5 } }}
                      >
                        View Downloads
                      </Button>
                    </Grid>

                    {showLicenseUi && (
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<KeyIcon />}
                          onClick={() => handleViewLicense(product)}
                          color="secondary"
                          size={window.innerWidth < 600 ? "medium" : "large"}
                          sx={{ py: { xs: 1, sm: 1.5 } }}
                        >
                          View License
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </CardActions>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* License Dialog */}
      <ProductLicenseDialog
        open={showLicenseDialog}
        onClose={() => setShowLicenseDialog(false)}
        product={selectedProduct}
      />
    </Container>
  );
};

export default ProductListPage;
